import moment from 'moment';
import { TOOLTIP_LABEL_FORMAT, isAppModule, CURRENT_PRODUCT_MODE } from '@utils/constants';
import { metricKeys } from '@pulse/metrics';
import { SEVERITY_CONFIG } from '@src/components/ai-alerts/ContentPanel/utils';
import { getSecsFromDuration } from '@src/components/ai-alerts/ContentPanel/TableContainer/utils';
import { DELETED_DIMENSION, HIDE_PEAK_VALUE_METRICS } from '@src/components/ai-alerts/constants';
import { getMetricDisplayName } from '@src/utils/metrics';

const getCauseKey = ({ dimensionName, rootCause }) => `${dimensionName}-${rootCause}`;

export const filterAlerts = (selectedAlert, alertsSummary) => {
  const selectedCause = getCauseKey(selectedAlert);
  const filteredAlerts: any = alertsSummary.filter((alert) => selectedCause === getCauseKey(alert));
  return filteredAlerts;
};

export const buildEventHeatMap = (alerts) => {
  const alertCount = {};
  alerts.forEach((alert) => {
    const day = moment(alert.eventTimestamp * 1000).format('YYYY-MM-DD');
    if (day in alertCount) {
      alertCount[day]++;
    } else {
      alertCount[day] = 1;
    }
  });
  return Object.entries(alertCount);
};

export const formatTimeInfo = ({ from, to }) => {
  return `Based on alert data from ${moment(from).format(TOOLTIP_LABEL_FORMAT)} to ${moment(to).format(TOOLTIP_LABEL_FORMAT)}`;
};

export const isCDNIssue = (metric, rootCauseWithoutFormatting) => {
  const isCDNIssue = rootCauseWithoutFormatting && rootCauseWithoutFormatting.includes('CDN');
  const isCDNMetric = [
    metricKeys.VideoPlaybackFailuresTechPercentage,
    metricKeys.ConnectionInducedRebufferingRatio,
  ].includes(metric);
  return isCDNIssue && isCDNMetric;
};

// This format fn is only used to show alert info in diagnostics table, check if formatAIAlertSummary can replace this fn
// formatting code is copied from the aiAlertEventDetail component
export const formatAlertSummary = (
  alert,
  flowId,
  frustrationSignalId,
  dimensionListObject = {},
  dimensionMapping = {},
) => {
  const {
    alertId,
    metricDisplayName,
    cumulativeImpactedUniques,
    cumulativeImpactedUniquesDisplay,
    cumulativeTotalUniques,
    cumulativeTotalUniquesDisplay,
    incrementalImpactedFlows,
    totalFlows,
    impactedCountDisplay,
    totalCountDisplay,
    alertEvents,
    peakValueDisplay,
    startTime,
    rootCauseDisplay,
    metricName,
    status,
    eventTime,
    metricUnit,
    peakEvent,
    dimensionName,
    rootCauseDisplayLabel,
    duration,
  } = alert;
  const formattedMetricDisplayName =
    getMetricDisplayName({
      metricKey: metricName,
      flowOverride: !!flowId,
      frustrationSignalOverride: !!frustrationSignalId,
    }) || metricDisplayName;
  // The last event's link will be provided for diagnosis in summary
  const lastEvent = alertEvents.length && alertEvents[0];
  const endTime = lastEvent.eventTime;
  const impactedPercentage =
    !!cumulativeImpactedUniques && !!cumulativeTotalUniques
      ? ((cumulativeImpactedUniques / cumulativeTotalUniques) * 100).toFixed(2)
      : 'NA';

  const impactedCountPercentage =
    !!incrementalImpactedFlows && !!totalFlows
      ? ((incrementalImpactedFlows / totalFlows) * 100).toFixed(2)
      : 'NA';

  let timeEndedText = 'Time issue ended';
  let alertEndedText = 'Ended Alert';

  if (isAppModule) {
    // APP
    if (alert.status === 'Active') {
      timeEndedText = `Time issue became ${alert.peakSeverity}`;
      alertEndedText = 'Active Alert';
    }
  } else {
    // EI AI
    if (alertEvents.length && lastEvent.eventType !== 'Ended') {
      timeEndedText = `Time issue became ${lastEvent.eventType}`;
      alertEndedText = 'Active Alert';
    }
  }

  const curProductMapping = dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()];

  // &&& is the splitter of different root causes and dimension names
  const rootCauseArray = rootCauseDisplayLabel.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');

  const displayRootCauses = rootCauseArray.map((rc, index) => {
    const pulse5DimensionMapping = curProductMapping?.[dimensionNameArray[index]];
    const dimensionLabel = pulse5DimensionMapping
      ? dimensionListObject[pulse5DimensionMapping.field] || pulse5DimensionMapping.pulse5Label
      : DELETED_DIMENSION;

    return {
      rootCause: dimensionLabel,
      value: rc,
    };
  });

  const durationWithoutSeconds = duration.replace(/ \d+s$/, '');

  const info: any = {
    alertId,
    metricDisplayName: formattedMetricDisplayName,
    rootCause: rootCauseDisplay,
    timeEndedText,
    alertEndedText,
    issueStartTime: startTime.replace(/, \d{4}/, ''),
    issueEndTime: endTime.replace(/, \d{4}/, ''),
    timeAlertFired: eventTime,
    dimensionName,
    rootCauseDisplayLabel,
    duration: durationWithoutSeconds,
    displayRootCauses,
    metricName,
    metricUnit,
  };

  // Upstream KPI label (e.g. "Conviva Good"), shown in the expanded card only when present.
  if (alert.kpiName) {
    info.kpiName = alert.kpiName;
  }

  const showAlertStatus = status !== 'NA';
  if (showAlertStatus) {
    info.status = status;
  }

  const isCDNIssueMetric = !isCDNIssue(metricName, rootCauseDisplay);
  const isHidePeakValueMetric = HIDE_PEAK_VALUE_METRICS.includes(metricName);
  if (isCDNIssueMetric && !isHidePeakValueMetric) {
    info.peakValue = `${peakValueDisplay}${metricUnit}`;
  }

  // cumulative impacted unique details are not available for pcp
  const showImpactedUniquesDetails = metricName !== metricKeys.ConcurrentPlays;
  if (showImpactedUniquesDetails) {
    info.cumulativeImpactedUniques = cumulativeImpactedUniquesDisplay;
    info.impactedPercentage = impactedPercentage;
    info.cumulativeTotalUniques = cumulativeTotalUniquesDisplay;
  }

  if (isAppModule && metricUnit === '%') {
    info.impactedCounts = impactedCountDisplay;
    info.impactedCountPercentage = impactedCountPercentage;
    info.totalCounts = totalCountDisplay;
  }
  return info;
};

export const sortColumn = (sortBy: any, dataSource) => {
  switch (sortBy.key) {
    case 'metricDisplayName':
    case 'rootCauseDisplay':
    case 'status':
    case 'accountName':
    case 'c3Name':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1[sortBy.key].toLowerCase() || '';
        const secondItem = item2[sortBy.key].toLowerCase() || '';
        return sortBy.order === 'asc'
          ? firstItem.localeCompare(secondItem)
          : secondItem.localeCompare(firstItem);
      });

      break;

    case 'cumulativeImpactedUniquesDisplay':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1.cumulativeImpactedUniques;
        const secondItem = item2.cumulativeImpactedUniques;
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;

    case 'eventTime':
      dataSource = dataSource.sort((item1, item2) => {
        const isSafari = window.safari !== undefined;
        if (isSafari) {
          const item1EventTime = moment(item1.eventTime, 'HH:mm MMM DD, YYYY').format();
          const item2EventTime = moment(item2.eventTime, 'HH:mm MMM DD, YYYY').format();
          return sortBy.order === 'asc'
            ? moment(item1EventTime).diff(item2EventTime)
            : moment(item2EventTime).diff(item1EventTime);
        } else {
          return sortBy.order === 'asc'
            ? moment(item1.eventTime).diff(item2.eventTime)
            : moment(item2.eventTime).diff(item1.eventTime);
        }
      });
      break;

    case 'peakSeverity':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = SEVERITY_CONFIG[item1[sortBy.key]].order;
        const secondItem = SEVERITY_CONFIG[item2[sortBy.key]].order;
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;

    case 'duration':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = getSecsFromDuration(item1[sortBy.key]);
        const secondItem = getSecsFromDuration(item2[sortBy.key]);
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;

    default:
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1[sortBy.key];
        const secondItem = item2[sortBy.key];
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;
  }
};
